import type { UniclientMessageFrame } from '@lemonade-hq/uniclient-chat';
import { useDelayedValue } from '@lemonade-hq/uniclient-theme';
import { useMemo } from 'react';
import { FRAME_TRANSITION_DURATION } from '../animators/durations';
import { formattedTextToString } from '../utils/parseFormattedTitle';
import type { WizardStep } from './useWizardResolvedSteps';

export interface HistoryEntry {
  readonly title: string;
  readonly value?: string;
  readonly name: string;
  readonly editable?: boolean;
  readonly id: string;
}

interface UseWizardHistory {
  readonly disabled: boolean;
  readonly history: HistoryEntry[];
}

interface UseWizardHistoryProps {
  readonly steps: WizardStep[];
  readonly currentStepIndex: number;
  readonly isTransitioningOut: boolean;
  readonly isTransitioningBetweenScripts: boolean;
}

export const useWizardHistory = ({
  steps,
  currentStepIndex,
  isTransitioningOut,
  isTransitioningBetweenScripts,
}: UseWizardHistoryProps): UseWizardHistory => {
  const history = useMemo(() => {
    const historySteps: HistoryEntry[] = [];

    const relevantSteps = steps.slice(0, currentStepIndex + 1);

    for (let i = relevantSteps.length - 1; i >= 0; i--) {
      const step = relevantSteps[i];
      if (step.frame == null || step.frame.type === 'page') {
        if (step.frame?.lockHistory === true) break;

        continue;
      }

      const messageFrame = step.frame as UniclientMessageFrame;

      historySteps.push({
        title:
          typeof messageFrame.title === 'string' ? messageFrame.title : formattedTextToString(messageFrame.title.text),
        value: step.output,
        name: step.name,
        editable: messageFrame.editable ?? step.output !== '',
        id: step.id,
      });

      if (step.frame.lockHistory === true) break;
    }

    return historySteps.reverse().slice(0, -1);
  }, [steps, currentStepIndex]);

  const currentStep = steps[currentStepIndex] as WizardStep | undefined;
  const lastStep = steps[steps.length - 1] as WizardStep | undefined;
  const stepToInspect = isTransitioningOut ? lastStep : currentStep;

  const delayedStepFrame = useDelayedValue(stepToInspect?.frame, stepToInspect?.frame, FRAME_TRANSITION_DURATION);

  const disabled =
    isTransitioningBetweenScripts || delayedStepFrame?.lockHistory === true || delayedStepFrame?.type === 'page';

  return {
    disabled,
    history,
  };
};
